import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionList from 'assets/components/data/themes/Theme.Collection.List';
import DataMap from 'assets/components/dataMap/DataMap';
import SortButton from 'assets/components/filters/sortButton/SortButton';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ProgressWindow, { addProgressToWindow } from 'assets/components/progressWindow/ProgressWindow';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import useApi from 'assets/hooks/api/useApi';
import { Roles } from 'assets/hooks/authInfo/useAuthInfo';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, defaultDateTimeFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { cleanValues } from 'assets/utils/data/Object';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import dayjs from 'dayjs';
import { isEmpty, merge } from 'lodash';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import VertaforeStatuses from 'models/enums/VertaforeDocumentStatuses';
import contractStatusFlowApi from 'models/sales/contract/statusFlow/Api.Contract.StatusFlow';
import React, { useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { SalesNewContractAction } from '../../newContract/Sales.NewContract.Hooks';
import { salesNewContractIndexRoute } from '../../newContract/Sales.NewContract.Index';
import { SalesContractAction, SalesContractVertaforeSyncType, useSalesContractProvider } from '../Sales.Contract.Hooks';
import ContractStatusStateMachine from '../other/Contract.StatusStateMachine';
import SalesContractFilters from './Sales.Contract.Filters';

export default function ContractList() {
  const { lang } = getLocales();
  const [newCreator, setNewCreator] = useState<{ contract: Model.IContract; user: Model.IUser }>(undefined);
  const { payload: statusInfo } = useApi({ action: contractStatusFlowApi });
  const {
    listApi,
    pageRouter,
    permissions,
    userListApi,
    contractUnknownVertaforeStatusListApi,
    contractNotUploadedVertaforeStatusListApi,
    exportContractsToExcel,
    changeContractCreator,
    syncAllWithVertafore,
    resendAllForSigning,
    reuploadAllSignedDocuments,
    isFetchingEnvelopes,
    isStatusChanging,
  } = useSalesContractProvider();
  const { redirect: redirectToNewContract } = usePageRouter<
    Module.Sales.NewContract.Params,
    Module.Sales.NewContract.Query
  >({ route: salesNewContractIndexRoute });

  function handleSort(newFilters: Api.IDefaultListRequest) {
    pageRouter.updateQuery({
      ...pageRouter.query,
      list: cleanValues({
        ...pageRouter.query?.list,
        ...newFilters,
      }),
    });
  }

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.contract}
      render={lang.noInfo}
    />
  ) : (
    <>
      <ProgressWindow />
      <Page
        class={ThemePageList}
        isLoading={listApi.isExecuting || isStatusChanging}
        header={
          <>
            <b style={{ marginRight: 'auto' }} children={lang.contract} />
            {isMobile && <SalesContractFilters />}
            &nbsp;
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              active
              media={<Icon class="fas-plus" />}
              render={!isMobile && lang.addNew}
              onClick={redirectToNewContract}
              disabled={!permissions.CREATE_CONTRACT}
              data={{ action: SalesNewContractAction.VEHICLE }}
            />
          </>
        }
        subHeader={
          <>
            <DropdownButton
              buttonMedia={<Icon class="fas-sync" />}
              horizontalPosition="auto_fixed"
              verticalPosition="auto_fixed"
              subcomponents={ThemeDropdownButtonPopup}
              buttonBody={lang.syncWithVertafore}
              htmlElementProps={{ title: lang.syncAllWithVertafore }}
              title={lang.syncAllWithVertafore}
              disabled={!permissions.VERTAFORE_SYNC_CONTRACT}
              tabIndex={null}
              dropdownOptions={() => {
                return (
                  <>
                    <Button
                      class={ThemeButtonMenuItem}
                      media={<Icon class="fas-sync" />}
                      render={lang.contracts}
                      onClick={({ addProgressToWindow, syncType }) =>
                        syncAllWithVertafore(addProgressToWindow, syncType)
                      }
                      data={{ addProgressToWindow, syncType: SalesContractVertaforeSyncType.SYNC_CONTRACTS }}
                      disabled={
                        !permissions.VERTAFORE_SYNC_CONTRACT || isEmpty(contractUnknownVertaforeStatusListApi?.payload)
                      }
                    />
                    <Button
                      class={ThemeButtonMenuItem}
                      media={<Icon class="fas-sync" />}
                      render={lang.contractsDocumentationAggregate}
                      onClick={({ addProgressToWindow, syncType }) =>
                        syncAllWithVertafore(addProgressToWindow, syncType)
                      }
                      disabled={
                        !permissions.VERTAFORE_SYNC_CONTRACT ||
                        isEmpty(contractNotUploadedVertaforeStatusListApi?.payload)
                      }
                      data={{ addProgressToWindow, syncType: SalesContractVertaforeSyncType.SYNC_CONTRACT_DOCUMENTS }}
                    />
                  </>
                );
              }}
            />
            &nbsp;
            <DropdownButton
              buttonMedia={<Icon class="fas-sync" />}
              horizontalPosition="auto_fixed"
              verticalPosition="auto_fixed"
              subcomponents={ThemeDropdownButtonPopup}
              buttonBody={lang.docusignOptions}
              htmlElementProps={{ title: lang.docusignOptions }}
              title={lang.docusignOptions}
              disabled={!(permissions?.SEND_CONTRACT_FOR_SIGNING || permissions?.REUPLOAD_SIGNED_CONTRACTDOCUMENT)}
              tabIndex={null}
              dropdownOptions={({ animateOut }) => {
                return (
                  <>
                    <Button
                      class={ThemeButtonMenuItem}
                      media={<Icon class="fas-sync" />}
                      render={!isMobile && lang.resendForSigning}
                      isLoading={isFetchingEnvelopes}
                      onClick={(data) => {
                        animateOut();
                        resendAllForSigning(data);
                      }}
                      data={addProgressToWindow}
                      disabled={!permissions.SEND_CONTRACT_FOR_SIGNING || isEmpty(listApi?.payload?.data)}
                    />
                    <Button
                      class={ThemeButtonMenuItem}
                      media={<Icon class="fas-sync" />}
                      render={!isMobile && lang.reuploadSignedDocuments}
                      isLoading={isFetchingEnvelopes}
                      onClick={(data) => {
                        animateOut();
                        reuploadAllSignedDocuments(data);
                      }}
                      data={addProgressToWindow}
                      disabled={!permissions.REUPLOAD_SIGNED_CONTRACTDOCUMENT || isEmpty(listApi?.payload?.data)}
                    />
                  </>
                );
              }}
            />
            &nbsp;
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              active
              media={<Icon class="fas-download" />}
              render={!isMobile && lang.export}
              onClick={exportContractsToExcel}
              disabled={!permissions.CREATE_CONTRACT || isEmpty(listApi?.payload?.data)}
            />
          </>
        }
        render={
          <>
            {newCreator && (
              <Confirm
                itemName={`${combineStrings(' ', newCreator.user.firstName, newCreator.user.lastName)}`}
                info={
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                      {lang.contractNumber}: {newCreator.contract.appNumber}
                    </span>
                    <span style={{ fontWeight: 700, color: 'yellow' }}>
                      This action may affect associated documents and other contract-related data.
                    </span>
                  </div>
                }
                actionName={`${lang.changeContractCreator} to: `}
                onClose={() => setNewCreator(undefined)}
                onConfirm={async ({ contract, user }) => {
                  await changeContractCreator({ contractId: contract.id, creatorId: user?.id });
                  setNewCreator(undefined);
                }}
                data={newCreator}
                subcomponents={ThemeConfirmDefault}
                confirmationType="nextButton"
              />
            )}
            <Collection class={ThemeCollectionList}>
              {isDesktop && <SalesContractFilters />}
              <table className={ThemeTableSimple}>
                <thead>
                  <tr>
                    <th>
                      {lang.appNumber}
                      <SortButton
                        orderBy="appNumber"
                        thenBy={['client.FirstName', 'client.LastName', 'vehicle.Vin']}
                        apiBody={listApi.body}
                        onSort={handleSort}
                      />
                    </th>
                    <th>
                      {lang.product}
                      <SortButton orderBy="product.name" apiBody={listApi.body} onSort={handleSort} />
                    </th>
                    <th>
                      {lang.vehicle}/{lang.vin}
                      <SortButton
                        orderBy="vehicle.makeCode"
                        thenBy="vehicle.model"
                        apiBody={listApi.body}
                        onSort={handleSort}
                      />
                    </th>
                    <th>
                      {lang.client}
                      <SortButton
                        orderBy="client.firstName"
                        thenBy="client.lastName"
                        apiBody={listApi.body}
                        onSort={handleSort}
                      />
                    </th>
                    <th>
                      {lang.dealer}
                      <SortButton orderBy="dealer.name" apiBody={listApi.body} onSort={handleSort} />
                    </th>
                    <th>
                      {lang.createdBy}
                      <SortButton
                        orderBy="createdBy.firstName"
                        thenBy="createdBy.lastName"
                        apiBody={listApi.body}
                        onSort={handleSort}
                      />
                    </th>
                    <th>
                      {lang.createdAt}
                      <SortButton orderBy="createdAtUtc" apiBody={listApi.body} onSort={handleSort} />
                    </th>
                    <th>
                      {lang.expiryDate}
                      <SortButton orderBy="expiryDate" apiBody={listApi.body} onSort={handleSort} />
                    </th>
                    <th>{lang.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  <DataMap
                    data={listApi.payload?.data}
                    render={({ data: contract }) => {
                      return (
                        <React.Fragment key={contract.id}>
                          <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                            <td
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: Theme.Size.XS,
                              }}
                            >
                              <span>
                                {contract?.isPayed && (
                                  <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.success }}>
                                    <Icon class="fas-check" />
                                    &nbsp; Paid
                                  </Box>
                                )}
                                {(contract?.isSignedManually ||
                                  contract?.isSignedWithDocuSing ||
                                  contract?.isSentForSigning) && (
                                  <Box
                                    class={ThemeTagRounded}
                                    style={{
                                      backgroundColor:
                                        contract?.isSignedManually || contract?.isSignedWithDocuSing
                                          ? Theme.Color.success
                                          : Theme.Color.warning,
                                    }}
                                  >
                                    <Icon class="fas-check" />
                                    &nbsp;
                                    {contract?.isSignedManually
                                      ? lang.signedManually
                                      : contract?.isSignedWithDocuSing
                                      ? lang.signedWithDocuSign
                                      : lang.sentForSigning}
                                  </Box>
                                )}
                                {!contract?.client?.vertaforeCustomerNumber ? (
                                  contract?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_CLIENT ? (
                                    <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                                      <Icon class="fas-ban" />
                                      &nbsp;
                                      {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
                                        status: 'FAILED',
                                      })}
                                    </Box>
                                  ) : (
                                    contract?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY && (
                                      <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                                        <Icon class="fas-ban" />
                                        &nbsp;
                                        {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
                                          status: 'CREATING POLICY',
                                        })}
                                      </Box>
                                    )
                                  )
                                ) : contract?.vertaforeStatus === VertaforeStatuses.ERROR_UPLOAD_DOCUMENT ? (
                                  <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                                    <Icon class="fas-ban" />
                                    &nbsp;
                                    {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                                      status: 'DOCUMENT UPLOAD FAILED',
                                    })}
                                  </Box>
                                ) : contract?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ? (
                                  <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                                    <Icon class="fas-ban" />
                                    &nbsp;
                                    {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                                      status: 'DOCUMENT NOT UPLOADED',
                                    })}
                                  </Box>
                                ) : contract?.vertaforeStatus === VertaforeStatuses.UNSIGNED_UPLOADED ||
                                  contract?.vertaforeStatus === VertaforeStatuses.SIGNED_UPLOADED ? (
                                  <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.success }}>
                                    <Icon class="fas-check" />
                                    &nbsp; {lang.syncedWithVertafore}
                                  </Box>
                                ) : contract?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY ? (
                                  <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                                    <Icon class="fas-ban" />
                                    &nbsp;
                                    {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
                                      status: 'CREATING POLICY',
                                    })}
                                  </Box>
                                ) : null}
                              </span>
                              <span>
                                <b>{contract.appNumberWithRevision}</b>
                                <br />
                                {contract.statusCode?.replace('_', ' - ')}
                              </span>
                            </td>
                            <td>{contract.product?.name || lang.unknownProduct}</td>
                            <td>
                              <span>
                                {contract.vehicle.makeCode} {contract.vehicle.model}
                              </span>
                              <span> {contract.vehicle.year?.toString()}</span>
                              <br />
                              <span>{contract.vehicle?.vin}</span>
                            </td>
                            <td>{combineStrings(' ', contract.client?.firstName, contract.client?.lastName)}</td>
                            <td>{contract.dealer?.name}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.XS }}>
                                <div>{combineStrings(' ', contract.createdBy?.fullName) || lang.unknownUser}</div>
                                <div>
                                  <DropdownButton
                                    buttonMedia={<Icon class="fas-user" />}
                                    horizontalPosition="auto_fixed"
                                    verticalPosition="auto_fixed"
                                    subcomponents={ThemeDropdownButtonPopup}
                                    htmlElementProps={{ title: lang.changeOwner }}
                                    title={lang.changeContractCreator}
                                    disabled={!permissions.CHANGE_CONTRACT_CREATOR}
                                    tabIndex={null}
                                    dropdownOptions={
                                      <DataMap
                                        data={userListApi?.payload?.data?.filter(
                                          (user) =>
                                            user?.roles?.some((r) => r.id === Roles.administrator) ||
                                            user?.dealers?.some((d) => d.id === contract?.dealerId)
                                        )}
                                        render={({ data: user }) => (
                                          <Button
                                            key={user.id}
                                            onClick={setNewCreator}
                                            data={{ contract, user }}
                                            render={user.displayInfo.title}
                                            class={ThemeButtonMenuItem}
                                          />
                                        )}
                                      />
                                    }
                                  />
                                </div>
                              </div>
                            </td>
                            <td>
                              {dayjs(contract?.createdAtLocal, defaultServerDateTimeFormat).format(
                                defaultDateTimeFormat
                              )}
                            </td>
                            <td>
                              {dayjs(contract?.expiryDate, defaultServerDateTimeFormat).format(defaultDateFormat)}
                            </td>
                            <td style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                              <ContractStatusStateMachine
                                statusInfo={statusInfo}
                                contractId={contract.id}
                                currentStatus={contract.statusCode ?? ContractStatusCode.OPEN_SUBMITTED}
                                onChange={() => listApi.execute((b) => b)}
                              />
                              <Button
                                class={ThemeButtonCircle}
                                media={<Icon class="fas-info-circle" />}
                                onClick={pageRouter.updateParams}
                                data={{ id: contract.id.toString(), action: SalesContractAction.INFO }}
                                htmlElementProps={{ title: lang.info }}
                              />
                              <LinkButton
                                class={ThemeButtonCircle}
                                url={contract.documentationAggregate?.url}
                                media={<Icon class="fas-download" />}
                                target="_blank"
                                disabled={!contract?.documentationAggregate?.url}
                                htmlElementProps={{
                                  title: !contract?.documentationAggregate?.url
                                    ? lang.missingContractDocument
                                    : lang.download,
                                }}
                              />
                              <Button
                                class={ThemeButtonCircle}
                                media={<Icon class="fas-edit" />}
                                onClick={redirectToNewContract}
                                disabled={
                                  ContractStatusCode.OPEN_SUBMITTED !== contract?.statusCode ||
                                  !permissions.EDIT_CONTRACT
                                }
                                data={{ action: SalesNewContractAction.VEHICLE, id: contract.id.toString() }}
                                htmlElementProps={{ title: lang.edit }}
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    }}
                  />
                </tbody>
              </table>
            </Collection>
          </>
        }
        footer={
          <Pagination
            class={ThemePaginationRounded}
            count={listApi.payload?.count}
            offset={listApi.body?.offset}
            limit={listApi.body?.limit}
            onLimitChange={(limit) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    limit,
                    offset: 0,
                  },
                })
              )
            }
            onOffsetChange={(offset) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    offset,
                  },
                })
              )
            }
          />
        }
      />
    </>
  );
}
